import { EventEmitter } from 'events';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import type {
  EthereumProviderOptions,
  IEthereumProvider as EvmProvider,
} from '@walletconnect/ethereum-provider';
import { WalletConnectAdapter } from '@tronweb3/tronwallet-adapter-walletconnect';
import { WalletConnectChainID } from '@tronweb3/walletconnect-tron';
import type { WalletConnectAdapterConfig } from '@tronweb3/tronwallet-adapter-walletconnect';

export type SupportedWalletConnectNetwork = 'evm' | 'tron';

export interface MultiWalletConnectOptions {
  /**
   * Optional shared WalletConnect projectId.
   * If present, will be injected into both EVM and TRON configs unless already set.
   */
  projectId?: string;
  evm: EthereumProviderOptions;
  tron: WalletConnectAdapterConfig;
}

export interface MultiWalletConnectState {
  evmConnected: boolean;
  tronConnected: boolean;
  evmAccounts: string[];
  tronAddress: string | null;
}

export interface MultiConnectResult {
  evm: { success: boolean; error?: unknown };
  tron: { success: boolean; error?: unknown };
  networks: SupportedWalletConnectNetwork[];
}

type WalletConnectModalState = { open?: boolean };

type WalletConnectModalBridge = {
  getState?: () => WalletConnectModalState;
  subscribeState?: (callback: (state: WalletConnectModalState) => void) => () => void;
};

/**
 * Small orchestrator that allows dApps to work with EVM + TRON WalletConnect clients
 * from one place on the same page.
 */
export class MultiWalletConnectProvider extends EventEmitter {
  public evm!: EvmProvider;
  public tron!: WalletConnectAdapter;

  private tronAddressFromSession: string | null = null;

  private constructor() {
    super();
  }

  static async init(options: MultiWalletConnectOptions): Promise<MultiWalletConnectProvider> {
    const provider = new MultiWalletConnectProvider();
    await provider.initialize(options);
    return provider;
  }

  async connect(network: SupportedWalletConnectNetwork): Promise<void> {
    if (network === 'evm') {
      await this.evm.connect();
      this.emit('connected', { network, accounts: this.evm.accounts });
      return;
    }

    await this.tron.connect();
    this.tronAddressFromSession = this.tron.address || this.tronAddressFromSession;
    this.emit('connected', { network, address: this.getTronAddress() });
  }

  /**
   * Connect provided networks from one call.
   *
   * When both EVM and TRON are requested we keep a single WalletConnect modal flow
   * (EVM modal) and include TRON namespace in the same WC session proposal.
   */
  async connectNetworks(networks: SupportedWalletConnectNetwork[]): Promise<MultiConnectResult> {
    if (!networks.length) {
      throw new Error('At least one network is required');
    }

    const uniqNetworks = [...new Set(networks)];
    const includesEvm = uniqNetworks.includes('evm');
    const includesTron = uniqNetworks.includes('tron');

    const byNetwork: Record<SupportedWalletConnectNetwork, { success: boolean; error?: unknown }> = {
      evm: { success: false },
      tron: { success: false },
    };

    if (includesEvm && includesTron) {
      try {
        await this.connectBothWithSingleModal();
        byNetwork.evm = { success: true };
        byNetwork.tron = { success: true };
      } catch (error) {
        byNetwork.evm = { success: false, error };
        byNetwork.tron = { success: false, error };
      }
    } else {
      const results: PromiseSettledResult<void>[] = [];
      for (const network of uniqNetworks) {
        results.push(
          await Promise.resolve(this.connect(network)).then(
            () => ({ status: 'fulfilled', value: undefined } as PromiseFulfilledResult<void>),
            (reason) => ({ status: 'rejected', reason } as PromiseRejectedResult),
          ),
        );
      }

      uniqNetworks.forEach((network, idx) => {
        const r = results[idx];
        byNetwork[network] =
          r.status === 'fulfilled' ? { success: true } : { success: false, error: r.reason };
      });
    }

    const result: MultiConnectResult = {
      networks: uniqNetworks,
      evm: byNetwork.evm,
      tron: byNetwork.tron,
    };

    this.emit('networksConnectionResult', result);

    if (!result.evm.success && !result.tron.success) {
      throw new Error('Failed to connect requested networks');
    }

    return result;
  }

  private async connectBothWithSingleModal(): Promise<void> {
    const evmAny = this.evm as any;
    const tronNamespace = {
      chains: [this.resolveTronCaipChainId()],
      methods: ['tron_signTransaction', 'tron_signMessage'],
      events: [],
    };

    const openModal = evmAny?.rpc?.showQrModal === true;
    if (openModal) {
      await evmAny?.modal?.open?.();
    }

    try {
      const connectPromise = evmAny.signer.connect({
        namespaces: {
          eip155: {
            chains: evmAny.rpc.chains,
            methods: evmAny.rpc.methods,
            events: evmAny.rpc.events,
            rpcMap: evmAny.rpc.rpcMap,
          },
        },
        optionalNamespaces: {
          tron: tronNamespace,
        },
      });
      const modalClosePromise = openModal ? this.createWalletConnectModalClosePromise(evmAny) : null;
      const session = await (modalClosePromise
        ? Promise.race([connectPromise, modalClosePromise])
        : connectPromise);

      if (!session) {
        throw new Error('WalletConnect session was not created');
      }

      this.applyEvmSessionState(session);

      this.applyTronSessionState(session);

      this.emit('connected', { network: 'evm', accounts: this.evm.accounts });
      this.emit('connected', { network: 'tron', address: this.getTronAddress() });
    } finally {
      if (openModal) {
        evmAny?.modal?.close?.();
      }
    }
  }

  private createWalletConnectModalClosePromise(evmClient: { modal?: WalletConnectModalBridge }): Promise<never> {
    return new Promise((_, reject) => {
      const modal = evmClient.modal;

      if (!modal?.subscribeState) {
        return;
      }

      let sawOpen = Boolean(modal.getState?.()?.open);
      let settled = false;

      const unsubscribe = modal.subscribeState((state) => {
        if (settled) return;

        if (state.open) {
          sawOpen = true;
          return;
        }

        if (sawOpen) {
          settled = true;
          unsubscribe();
          reject(new Error('WalletConnect modal was closed'));
        }
      });
    });
  }


  private getTronAddress(): string | null {
    return this.tron.address || this.tronAddressFromSession;
  }

  private applyTronSessionState(session: any): void {
    const tronNamespace = session?.namespaces?.tron;
    const accounts: string[] = Array.isArray(tronNamespace?.accounts) ? tronNamespace.accounts : [];
    const first = accounts.find((account) => typeof account === 'string' && account.startsWith('tron:'));

    if (!first) {
      this.tronAddressFromSession = null;
      return;
    }

    const addr = first.split(':')[2];
    this.tronAddressFromSession = addr || null;
  }

  private applyEvmSessionState(session: any): void {
    const eipNamespace = session?.namespaces?.eip155;
    const accounts: string[] = Array.isArray(eipNamespace?.accounts) ? eipNamespace.accounts : [];
    const parsedAccounts = accounts
      .filter((account) => typeof account === 'string' && account.startsWith('eip155:'))
      .map((account) => account.split(':')[2])
      .filter((addr): addr is string => Boolean(addr));

    this.evm.accounts = parsedAccounts;

    const first = accounts[0];
    if (typeof first === 'string') {
      const chainHex = first.split(':')[1];
      const chainId = Number(chainHex);
      if (Number.isFinite(chainId) && chainId > 0) {
        this.evm.chainId = chainId;
      }
    }
  }

  private resolveTronCaipChainId(): string {
    const tronAny = this.tron as any;
    const rawNetwork = tronAny?._config?.network;

    if (typeof rawNetwork === 'string' && rawNetwork.startsWith('tron:')) {
      return rawNetwork;
    }

    if (rawNetwork === 'Mainnet' || rawNetwork === 'Shasta' || rawNetwork === 'Nile') {
      return WalletConnectChainID[rawNetwork];
    }

    if (typeof rawNetwork === 'string' && rawNetwork.startsWith('0x')) {
      return `tron:${rawNetwork}`;
    }

    return WalletConnectChainID.Nile;
  }

  /**
   * Backward compatibility helper for old API.
   * Connects both networks from a single call.
   */
  async connectBoth(): Promise<MultiConnectResult> {
    return this.connectNetworks(['tron', 'evm']);
  }

  async disconnect(network?: SupportedWalletConnectNetwork): Promise<void> {
    if (!network || network === 'evm') {
      this.evm.disconnect();
      this.emit('disconnected', { network: 'evm' });
    }

    if (!network || network === 'tron') {
      await this.tron.disconnect();
      this.tronAddressFromSession = null;
      this.emit('disconnected', { network: 'tron' });
    }
  }

  getState(): MultiWalletConnectState {
    return {
      evmConnected: Boolean(this.evm.session),
      tronConnected: this.tron.connected || Boolean(this.tronAddressFromSession),
      evmAccounts: this.evm.accounts,
      tronAddress: this.getTronAddress(),
    };
  }

  private async initialize(options: MultiWalletConnectOptions): Promise<void> {
    const evmConfig = this.withProjectId(options.evm, options.projectId);
    const tronConfig = this.withTronProjectId(options.tron, options.projectId);

    this.evm = await EthereumProvider.init(evmConfig);
    this.tron = new WalletConnectAdapter(tronConfig);

    this.evm.on('accountsChanged', (accounts: string[]) => {
      this.emit('accountsChanged', { network: 'evm', accounts });
    });

    this.evm.on('chainChanged', (chainId: string) => {
      this.emit('chainChanged', { network: 'evm', chainId });
    });

    this.evm.on('disconnect', () => {
      this.emit('disconnected', { network: 'evm' });
    });

    this.tron.on('accountsChanged', (currentAddress: string, previousAddress: string) => {
      this.emit('accountsChanged', {
        network: 'tron',
        currentAddress,
        previousAddress,
      });
    });

    this.tron.on('disconnect', () => {
      this.tronAddressFromSession = null;
      this.emit('disconnected', { network: 'tron' });
    });
  }

  private withProjectId(
    evmConfig: EthereumProviderOptions,
    projectId?: string,
  ): EthereumProviderOptions {
    if (evmConfig.projectId || !projectId) {
      return evmConfig;
    }

    return {
      ...evmConfig,
      projectId,
    };
  }

  private withTronProjectId(
    tronConfig: WalletConnectAdapterConfig,
    projectId?: string,
  ): WalletConnectAdapterConfig {
    const hasTronProjectId =
      typeof tronConfig.options === 'object' &&
      !!tronConfig.options &&
      'projectId' in tronConfig.options &&
      Boolean((tronConfig.options as { projectId?: string }).projectId);

    if (hasTronProjectId || !projectId) {
      return tronConfig;
    }

    return {
      ...tronConfig,
      options: {
        ...tronConfig.options,
        projectId,
      },
    };
  }
}

export default MultiWalletConnectProvider;
